/**
 * Supply — combat power, not a parts list.
 *
 * A parts table is a spreadsheet. This screen exists to show consequences, so
 * the ranking of the page is the ranking of a decision: what the fleet can do
 * today, what each shortage is costing per day, what buys the most force back,
 * and only then the rows a forward operator needs to check their own shelf.
 *
 * Three rules run through every panel here, and each one is a failure that has
 * happened to somebody:
 *
 *   A null is an unknown. `readinessRate` is null for an empty fleet, and a
 *   fleet of nothing rendered as "100% READY" is the single most dangerous
 *   thing this screen could say. It reads "—", with the reason next to it.
 *
 *   The backend ranking is the ranking. `impacts` arrives with parts that have
 *   no vendor sorted FIRST — an unknown that is grounding aircraft outranks a
 *   known 12-day wait — so this screen renders the array in the order it was
 *   given and never sorts it by units short.
 *
 *   Nothing here orders anything. The recovery list drafts a requisition and
 *   says so in its own header; placing it is a human act outside this system.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  type Part,
  type ReadinessImpact,
  type RiskFinding,
  type StockItem,
} from '@/api/supply';
import { MapCanvas } from '@/map/MapCanvas';
import { SupplySiteHoverLabel, useSupplyLayer } from '@/map/useSupplyLayer';
import { useHasRole } from '@/stores/authStore';
import {
  supplyActions,
  useSupplyAssets,
  useSupplyError,
  useSupplyFeasibility,
  useSupplyLocations,
  useSupplyOrders,
  useSupplyOverview,
  useSupplyParts,
  useSupplyPlatforms,
  useSupplyProfiles,
  useSupplyStock,
  useSupplyVendors,
} from '@/stores/supplyStore';

import { SupplyImportPanel } from './SupplyImportPanel';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const WHITE = '#f4f4f5';
const MUTED = 'rgba(244,244,245,0.5)';
const CARD = 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))';

/**
 * Supply moves in days, not frames. 20 s keeps a commander's figures current
 * across a shift change without putting the depot on the telemetry hot path.
 */
const REFRESH_MS = 20_000;

/** The one place a null becomes text. Never 0, never 100%. */
const UNKNOWN = '—';

function count(value: number, digits = 0): string {
  return value.toFixed(digits);
}

function readinessText(rate: number | null): string {
  return rate === null ? UNKNOWN : `${Math.round(rate * 100)}%`;
}

function readinessTone(rate: number | null): string {
  if (rate === null) return MUTED;
  if (rate >= 0.8) return OK;
  return rate >= 0.5 ? WARN : DANGER;
}

function Panel({
  title,
  right,
  subtitle,
  children,
}: {
  title: string;
  right?: ReactNode;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section
      className="rounded-[18px] border"
      style={{
        background: CARD,
        borderColor: 'rgba(255,255,255,0.13)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)',
        padding: '16px 18px',
      }}
      aria-label={title}
    >
      <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <h2 className="label-mono text-[9.5px]" style={{ color: MUTED, letterSpacing: '0.16em' }}>
          {title}
        </h2>
        {right}
      </div>
      {subtitle ? (
        <p className="mb-3 text-[10.5px]" style={{ color: 'rgba(244,244,245,0.42)' }}>
          {subtitle}
        </p>
      ) : (
        <div className="mb-3" />
      )}
      {children}
    </section>
  );
}

// ── Commander roll-up ───────────────────────────────────────────────────────

/**
 * Fleet readiness and the down-days figure, biggest things on the page.
 *
 * Down days is the Air-style number: not "four motors short" but the days of
 * aircraft availability the unit has already lost. It is null when no down
 * aircraft carries a known down-since, and null reads UNKNOWN, because a
 * shortage nobody has dated is not a shortage that cost nothing.
 */
function CommanderRollup() {
  const overview = useSupplyOverview();
  if (!overview) return null;
  const { fleet, platforms } = overview;
  const tone = readinessTone(fleet.readinessRate);
  const empty = fleet.total === 0;

  return (
    <section
      className="rounded-[18px] border"
      style={{
        background: CARD,
        borderColor: `color-mix(in oklch, ${tone} 40%, transparent)`,
        padding: '18px 20px',
      }}
      aria-label="Fleet readiness"
    >
      <div className="flex flex-wrap items-start gap-x-9 gap-y-4">
        <div className="flex flex-col">
          <span className="label-mono text-[9px]" style={{ color: MUTED, letterSpacing: '0.14em' }}>
            FLEET READINESS
          </span>
          <span className="tnum text-[46px] font-semibold leading-none" style={{ color: tone }}>
            {readinessText(fleet.readinessRate)}
          </span>
          <span className="label-mono mt-1 text-[9px]" style={{ color: MUTED }}>
            {empty ? 'NO FLEET ON THE REGISTER' : `${fleet.ready + fleet.degraded} OF ${fleet.total} AVAILABLE`}
          </span>
        </div>

        <div className="flex flex-col">
          <span className="label-mono text-[9px]" style={{ color: MUTED, letterSpacing: '0.14em' }}>
            DOWN-DAYS LOST
          </span>
          <span
            className="tnum text-[46px] font-semibold leading-none"
            style={{ color: fleet.downDaysTotal === null ? MUTED : DANGER }}
          >
            {fleet.downDaysTotal === null ? UNKNOWN : count(fleet.downDaysTotal)}
          </span>
          <span className="label-mono mt-1 text-[9px]" style={{ color: MUTED }}>
            {fleet.downDaysTotal === null
              ? 'NOT DATED — NO DOWN-SINCE ON RECORD'
              : 'AIRCRAFT-DAYS ALREADY LOST'}
          </span>
        </div>

        <div className="flex gap-6">
          {(
            [
              ['READY', fleet.ready, OK],
              ['DEGRADED', fleet.degraded, WARN],
              ['DOWN', fleet.down, DANGER],
              ['MAINT', fleet.inMaintenance, MUTED],
            ] as const
          ).map(([label, value, color]) => (
            <div key={label} className="flex flex-col items-start">
              <span className="tnum text-[26px] font-semibold leading-none" style={{ color }}>
                {value}
              </span>
              <span className="label-mono mt-1 text-[8.5px]" style={{ color: MUTED }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 border-t pt-4" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
        <span className="label-mono text-[9px]" style={{ color: MUTED, letterSpacing: '0.14em' }}>
          WHAT WE CAN BUILD FROM STOCK ON HAND
        </span>
        {platforms.length === 0 ? (
          <p className="mt-2 text-[11px]" style={{ color: MUTED }}>
            No platforms on the books.
          </p>
        ) : (
          <div
            className="mt-2.5 grid gap-2.5"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(230px, 100%), 1fr))' }}
          >
            {platforms.map((platform) => (
              <div
                key={platform.platformId}
                className="rounded-[12px] border px-3 py-2.5"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  borderColor:
                    platform.buildable === 0
                      ? 'color-mix(in oklch, oklch(0.72 0.19 28) 40%, transparent)'
                      : 'rgba(255,255,255,0.1)',
                }}
              >
                <div className="flex items-baseline gap-2">
                  <span className="label-mono text-[10.5px]" style={{ color: WHITE }}>
                    {platform.name}
                  </span>
                  <span className="label-mono ml-auto text-[8.5px]" style={{ color: MUTED }}>
                    {platform.readyNow} FLYING
                  </span>
                </div>
                <div className="mt-1.5 flex items-baseline gap-2">
                  <span
                    className="tnum text-[24px] font-semibold leading-none"
                    style={{ color: platform.buildable === 0 ? DANGER : OK }}
                  >
                    {platform.buildable}
                  </span>
                  <span className="label-mono text-[9px]" style={{ color: MUTED }}>
                    CAN BUILD
                  </span>
                </div>
                <p className="mt-1.5 text-[10.5px]" style={{ color: 'rgba(244,244,245,0.68)' }}>
                  {platform.bindingPartId
                    ? `Blocked on ${platform.bindingPartId}`
                    : 'Nothing blocking the next build'}
                </p>
                {platform.unverifiedPartIds.length > 0 ? (
                  <p className="label-mono mt-1 text-[8.5px]" style={{ color: WARN }}>
                    UNVERIFIED COUNTS: {platform.unverifiedPartIds.join(', ')}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ── What it's costing ───────────────────────────────────────────────────────

/** The consequence, as a sentence. Never just "short 4". */
function impactSegments(impact: ReadinessImpact): string[] {
  const segments = [
    `${impact.assetsDown.length} aircraft down`,
    `${count(impact.sortiesPerDayLost)} sorties/day lost`,
  ];
  if (impact.daysToRecover !== null) {
    segments.push(`whole in ${impact.daysToRecover} days`);
  }
  if (impact.projectedSortiesLost !== null) {
    segments.push(`${count(impact.projectedSortiesLost)} sorties lost before then`);
  }
  return segments;
}

function ImpactList({ impacts, nameOf }: { impacts: ReadinessImpact[]; nameOf: (id: string) => string }) {
  if (impacts.length === 0) {
    return (
      <p className="text-[11px]" style={{ color: MUTED }}>
        Nothing on the shelf is grounding an aircraft. No shortage is costing sorties today.
      </p>
    );
  }

  return (
    <ol className="flex flex-col gap-2">
      {/* Rendered in the order the server ranked them. A part nobody can source
          sits at the top by design, and re-sorting here by units short would
          bury the only shortage with no end date. */}
      {impacts.map((impact, index) => {
        const stranded = impact.daysToRecover === null;
        return (
          <li
            key={impact.partId}
            data-testid="impact-row"
            data-part-id={impact.partId}
            className="rounded-[13px] border px-[13px] py-[11px]"
            style={{
              background: stranded
                ? 'color-mix(in oklch, oklch(0.62 0.20 28) 14%, transparent)'
                : 'rgba(255,255,255,0.04)',
              borderColor: stranded
                ? 'color-mix(in oklch, oklch(0.62 0.20 28) 62%, transparent)'
                : 'rgba(255,255,255,0.11)',
            }}
          >
            <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
              <span className="tnum text-[10px]" style={{ color: MUTED }}>
                {index + 1}
              </span>
              <span className="label-mono text-[12px]" style={{ color: WHITE }}>
                {nameOf(impact.partId)}
              </span>
              <span className="tnum text-[10.5px]" style={{ color: MUTED }}>
                SHORT {impact.unitsShort}
              </span>
              {stranded ? (
                <span
                  className="label-mono rounded-[6px] px-2 py-[3px] text-[9.5px] font-semibold"
                  style={{
                    color: DANGER,
                    background: 'color-mix(in oklch, oklch(0.62 0.20 28) 26%, transparent)',
                    letterSpacing: '0.1em',
                  }}
                >
                  NO VENDOR ON RECORD
                </span>
              ) : null}
            </div>

            <p className="mt-1.5 text-[13px]" style={{ color: stranded ? WHITE : 'rgba(244,244,245,0.82)' }}>
              {impactSegments(impact).map((segment, i) => (
                <span key={segment}>
                  {i > 0 ? <span style={{ color: MUTED }}> · </span> : null}
                  {segment}
                </span>
              ))}
            </p>

            {stranded ? (
              <p className="mt-1 text-[11px]" style={{ color: DANGER }}>
                No supplier is on record for this part, so there is no date on which it comes
                whole. This is the worst finding on the page, not a missing field.
              </p>
            ) : null}

            <p className="mt-1 text-[10.5px]" style={{ color: 'rgba(244,244,245,0.45)' }}>
              {impact.detail}
            </p>
            {impact.assetsDown.length > 0 ? (
              <p className="label-mono mt-1 text-[9px]" style={{ color: MUTED }}>
                GROUNDED: {impact.assetsDown.join(', ')}
              </p>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

// ── Order list ──────────────────────────────────────────────────────────────

function RecoveryList({ nameOf }: { nameOf: (id: string) => string }) {
  const overview = useSupplyOverview();
  const recovery = overview?.recovery ?? [];

  if (recovery.length === 0) {
    return (
      <p className="text-[11px]" style={{ color: MUTED }}>
        Nothing to order. No shortage on the books buys an aircraft back today.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {recovery.map((plan) => (
        <li
          key={plan.partId}
          className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-[13px] border px-[13px] py-[11px]"
          style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.11)' }}
        >
          <span className="tnum text-[20px] font-semibold" style={{ color: WHITE }}>
            {plan.orderQuantity}
          </span>
          <span className="label-mono text-[11.5px]" style={{ color: WHITE }}>
            {nameOf(plan.partId)}
          </span>
          <span className="text-[11px]" style={{ color: MUTED }}>
            {plan.vendorId === null ? 'no vendor on record' : `from ${plan.vendorId}`}
            {' · '}
            {plan.daysToArrive === null ? 'no arrival date' : `${plan.daysToArrive} days out`}
          </span>
          <span className="ml-auto text-[12px]" style={{ color: OK }}>
            +{count(plan.sortiesPerDayRegained)} sorties/day
            {plan.assetsRecovered.length > 0
              ? `, recovers ${plan.assetsRecovered.join(', ')}`
              : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ── Risk ────────────────────────────────────────────────────────────────────

function severityTone(severity: number): string {
  return severity >= 5 ? DANGER : severity >= 3 ? WARN : MUTED;
}

function RiskList({ risks, nameOf }: { risks: RiskFinding[]; nameOf: (id: string) => string }) {
  if (risks.length === 0) {
    return (
      <p className="text-[11px]" style={{ color: MUTED }}>
        No supply risk on record. Nothing is single-sourced, long-lead, unverified or expired.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1.5">
      {risks.map((risk) => {
        const tone = severityTone(risk.severity);
        return (
          <li
            key={`${risk.partId}-${risk.kind}`}
            className="flex items-baseline gap-2.5 rounded-[11px] border px-[11px] py-[8px]"
            style={{
              background: 'rgba(255,255,255,0.04)',
              borderColor: `color-mix(in oklch, ${tone} 32%, transparent)`,
            }}
          >
            <span
              className="label-mono shrink-0 rounded-[5px] px-1.5 py-[2px] text-[8.5px]"
              style={{ color: tone, background: `color-mix(in oklch, ${tone} 18%, transparent)` }}
            >
              {risk.kind.toUpperCase()}
            </span>
            <span className="label-mono shrink-0 text-[10.5px]" style={{ color: WHITE }}>
              {nameOf(risk.partId)}
            </span>
            <span className="text-[11px]" style={{ color: 'rgba(244,244,245,0.7)' }}>
              {risk.detail}
            </span>
            <span className="tnum ml-auto shrink-0 text-[10px]" style={{ color: tone }}>
              SEV {risk.severity}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// ── Feasibility ask ─────────────────────────────────────────────────────────

function FeasibilityAsk() {
  const profiles = useSupplyProfiles();
  const { result, busy } = useSupplyFeasibility();
  const [profileId, setProfileId] = useState('');
  const [wanted, setWanted] = useState('');

  const chosen = profileId || profiles[0]?.profileId || '';

  const ask = () => {
    if (!chosen) return;
    const parsed = Number.parseInt(wanted, 10);
    void supplyActions.askFeasibility(
      Number.isFinite(parsed) && parsed > 0 ? { profileId: chosen, wanted: parsed } : { profileId: chosen },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="label-mono text-[8.5px]" style={{ color: MUTED }}>
            SORTIE PROFILE
          </span>
          <select
            aria-label="Sortie profile"
            value={chosen}
            onChange={(event) => setProfileId(event.target.value)}
            className="well rounded-[8px] px-2.5 py-1.5 text-[12px] outline-none"
            style={{ color: WHITE }}
          >
            {profiles.length === 0 ? <option value="">No profiles on the books</option> : null}
            {profiles.map((profile) => (
              <option key={profile.profileId} value={profile.profileId}>
                {profile.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="label-mono text-[8.5px]" style={{ color: MUTED }}>
            HOW MANY DO YOU WANT
          </span>
          <input
            aria-label="How many do you want"
            inputMode="numeric"
            value={wanted}
            onChange={(event) => setWanted(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') ask();
            }}
            placeholder="all we can"
            className="well w-[130px] rounded-[8px] px-2.5 py-1.5 tnum text-[12px] outline-none"
          />
        </label>

        <button
          type="button"
          onClick={ask}
          disabled={busy || !chosen}
          className="rounded-[8px] border px-3.5 py-1.5 label-mono text-[9.5px] disabled:opacity-40"
          style={{ borderColor: 'rgba(255,255,255,0.22)', color: WHITE }}
        >
          {busy ? 'ASKING…' : 'ASK'}
        </button>
      </div>

      {result ? (
        <div
          className="rounded-[13px] border px-[13px] py-[11px]"
          style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.13)' }}
        >
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="tnum text-[34px] font-semibold leading-none" style={{ color: result.sorties > 0 ? OK : DANGER }}>
              {result.sorties}
            </span>
            <span className="label-mono text-[10px]" style={{ color: MUTED }}>
              SORTIES POSSIBLE
            </span>
            <span className="text-[11.5px]" style={{ color: 'rgba(244,244,245,0.72)' }}>
              {result.bindingPartId
                ? `Binding part: ${result.bindingPartId}`
                : 'No part binds this profile'}
            </span>
            <span className="label-mono ml-auto text-[9.5px]" style={{ color: MUTED }}>
              {result.platformsAvailable} AIRFRAMES AVAILABLE
            </span>
          </div>

          {result.shortfalls.length > 0 ? (
            <ul className="mt-2 flex flex-col gap-1">
              {result.shortfalls.map((shortfall) => (
                <li key={shortfall.partId} className="tnum text-[11px]" style={{ color: WARN }}>
                  {shortfall.partId}: need {shortfall.needed}, have {shortfall.available}, short{' '}
                  {shortfall.shortBy}
                </li>
              ))}
            </ul>
          ) : null}

          {/* Verbatim. These notes are the caveats on the number above them and
              paraphrasing one is how a caveat quietly stops applying. */}
          {result.notes.length > 0 ? (
            <ul className="mt-2 flex flex-col gap-1">
              {result.notes.map((note) => (
                <li key={note} className="text-[11px]" style={{ color: 'rgba(244,244,245,0.6)' }}>
                  {note}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ── Forward operator detail ─────────────────────────────────────────────────

const TABS = ['STOCK', 'PARTS', 'VENDORS', 'PLATFORMS', 'ASSETS', 'ORDERS', 'SITES'] as const;
type Tab = (typeof TABS)[number];

const CELL = 'px-2.5 py-1.5 text-left align-top';

function TableShell({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <div className="scroll-thin max-h-[430px] overflow-auto">
      <table className="w-full border-collapse text-[11.5px]">
        <thead>
          <tr>
            {head.map((label) => (
              <th
                key={label}
                scope="col"
                className={`${CELL} label-mono sticky top-0 text-[8.5px]`}
                style={{ color: MUTED, background: '#141416', letterSpacing: '0.12em' }}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function rowStyle(index: number): { background: string } {
  return { background: index % 2 ? 'rgba(255,255,255,0.03)' : 'transparent' };
}

/**
 * Stock, by location.
 *
 * A row nobody has physically counted is marked UNVERIFIED and never styled as
 * a confirmed figure. The operator affordance is deliberately "COUNTED" rather
 * than "save": the write only claims verification when a human says they walked
 * the shelf, which is the whole difference between a record and a measurement.
 */
function StockTable() {
  const stock = useSupplyStock();
  const locations = useSupplyLocations();
  const parts = useSupplyParts();
  const canEdit = useHasRole('operator');
  const [locationId, setLocationId] = useState('');
  const [editing, setEditing] = useState<{ partId: string; locationId: string } | null>(null);
  const [draft, setDraft] = useState('');

  const nameOf = useMemo(() => new Map(parts.map((part) => [part.partId, part.name])), [parts]);
  const locationName = useMemo(
    () => new Map(locations.map((location) => [location.locationId, location.name])),
    [locations],
  );

  const rows = locationId ? stock.filter((item) => item.locationId === locationId) : stock;

  const commit = (item: StockItem) => {
    const quantity = Number.parseInt(draft, 10);
    if (!Number.isFinite(quantity) || quantity < 0) return;
    setEditing(null);
    void supplyActions.saveStock({
      partId: item.partId,
      locationId: item.locationId,
      quantity,
      condition: item.condition,
      lot: item.lot,
      verified: true,
    });
  };

  return (
    <div className="flex flex-col gap-2.5">
      <label className="flex items-center gap-2">
        <span className="label-mono text-[8.5px]" style={{ color: MUTED }}>
          LOCATION
        </span>
        <select
          aria-label="Filter stock by location"
          value={locationId}
          onChange={(event) => setLocationId(event.target.value)}
          className="well rounded-[8px] px-2.5 py-1.5 text-[11.5px] outline-none"
          style={{ color: WHITE }}
        >
          <option value="">All locations</option>
          {locations.map((location) => (
            <option key={location.locationId} value={location.locationId}>
              {location.name}
            </option>
          ))}
        </select>
        <span className="label-mono text-[9px]" style={{ color: MUTED }}>
          {rows.length} LINE ITEMS
        </span>
      </label>

      {rows.length === 0 ? (
        <p className="text-[11px]" style={{ color: MUTED }}>
          No stock recorded here.
        </p>
      ) : (
        <TableShell head={['PART', 'LOCATION', 'QTY', 'CONDITION', 'LOT', 'COUNT']}>
          {rows.map((item, index) => {
            const unverified = item.verifiedAt === null;
            const isEditing =
              editing?.partId === item.partId && editing.locationId === item.locationId;
            const label = nameOf.get(item.partId) ?? item.partId;
            return (
              <tr key={`${item.partId}@${item.locationId}@${item.lot}`} style={rowStyle(index)}>
                <td className={CELL} style={{ color: WHITE }}>
                  {label}
                </td>
                <td className={CELL} style={{ color: 'rgba(244,244,245,0.65)' }}>
                  {locationName.get(item.locationId) ?? item.locationId}
                </td>
                <td className={`${CELL} tnum`} style={{ color: unverified ? WARN : WHITE }}>
                  {item.quantity}
                </td>
                <td className={CELL} style={{ color: 'rgba(244,244,245,0.65)' }}>
                  {item.condition}
                </td>
                <td className={CELL} style={{ color: 'rgba(244,244,245,0.5)' }}>
                  {item.lot || UNKNOWN}
                </td>
                <td className={CELL}>
                  {unverified ? (
                    <span
                      className="label-mono rounded-[5px] px-1.5 py-[2px] text-[8.5px]"
                      style={{
                        color: WARN,
                        background: 'color-mix(in oklch, oklch(0.80 0.15 85) 20%, transparent)',
                      }}
                    >
                      UNVERIFIED
                    </span>
                  ) : (
                    <span className="label-mono text-[8.5px]" style={{ color: OK }}>
                      VERIFIED
                    </span>
                  )}
                  {canEdit ? (
                    isEditing ? (
                      <span className="ml-2 inline-flex items-center gap-1.5">
                        <input
                          aria-label={`Counted quantity for ${label}`}
                          autoFocus
                          inputMode="numeric"
                          value={draft}
                          onChange={(event) => setDraft(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') commit(item);
                            if (event.key === 'Escape') setEditing(null);
                          }}
                          className="well w-[64px] rounded-[6px] px-1.5 py-0.5 tnum text-[11px] outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => commit(item)}
                          className="label-mono text-[8.5px]"
                          style={{ color: OK }}
                        >
                          SAVE
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        aria-label={`Record a physical count for ${label}`}
                        onClick={() => {
                          setEditing({ partId: item.partId, locationId: item.locationId });
                          setDraft(String(item.quantity));
                        }}
                        className="label-mono ml-2 text-[8.5px]"
                        style={{ color: MUTED }}
                      >
                        COUNTED
                      </button>
                    )
                  ) : null}
                </td>
              </tr>
            );
          })}
        </TableShell>
      )}
    </div>
  );
}

function PartsTable() {
  const parts = useSupplyParts();
  if (parts.length === 0) {
    return <p className="text-[11px]" style={{ color: MUTED }}>No parts on the books.</p>;
  }
  return (
    <TableShell head={['PART', 'NUMBER', 'CATEGORY', 'CRITICALITY', 'UNIT COST', 'VENDORS', 'SHELF LIFE']}>
      {parts.map((part, index) => (
        <tr key={part.partId} style={rowStyle(index)}>
          <td className={CELL} style={{ color: WHITE }}>{part.name}</td>
          <td className={`${CELL} tnum`} style={{ color: 'rgba(244,244,245,0.65)' }}>{part.partNumber}</td>
          <td className={CELL} style={{ color: 'rgba(244,244,245,0.65)' }}>{part.category}</td>
          <td className={CELL} style={{ color: part.criticality === 'mission-critical' ? DANGER : MUTED }}>
            {part.criticality}
          </td>
          <td className={`${CELL} tnum`} style={{ color: 'rgba(244,244,245,0.65)' }}>
            ${part.unitCost.toFixed(2)}
          </td>
          {/* No vendor is not an empty cell. It is the reason a shortage has no
              end date, and it says so here as well as in the impact list. */}
          <td className={CELL} style={{ color: part.vendorIds.length === 0 ? DANGER : 'rgba(244,244,245,0.65)' }}>
            {part.vendorIds.length === 0 ? 'NO VENDOR ON RECORD' : part.vendorIds.join(', ')}
          </td>
          <td className={`${CELL} tnum`} style={{ color: 'rgba(244,244,245,0.5)' }}>
            {part.shelfLifeDays === null ? UNKNOWN : `${part.shelfLifeDays} d`}
          </td>
        </tr>
      ))}
    </TableShell>
  );
}

function VendorsTable() {
  const vendors = useSupplyVendors();
  if (vendors.length === 0) {
    return <p className="text-[11px]" style={{ color: MUTED }}>No vendors on the books.</p>;
  }
  return (
    <TableShell head={['VENDOR', 'LEAD TIME', 'ON-TIME RATE']}>
      {vendors.map((vendor, index) => (
        <tr key={vendor.vendorId} style={rowStyle(index)}>
          <td className={CELL} style={{ color: WHITE }}>{vendor.name}</td>
          <td className={`${CELL} tnum`} style={{ color: vendor.leadTimeDays >= 30 ? WARN : 'rgba(244,244,245,0.65)' }}>
            {vendor.leadTimeDays} d
          </td>
          {/* Never measured is not a perfect record. */}
          <td className={`${CELL} tnum`} style={{ color: vendor.onTimeRate === null ? MUTED : WHITE }}>
            {vendor.onTimeRate === null
              ? `${UNKNOWN} NEVER MEASURED`
              : `${Math.round(vendor.onTimeRate * 100)}%`}
          </td>
        </tr>
      ))}
    </TableShell>
  );
}

function PlatformsTable() {
  const platforms = useSupplyPlatforms();
  const parts = useSupplyParts();
  const nameOf = useMemo(() => new Map(parts.map((part) => [part.partId, part.name])), [parts]);
  if (platforms.length === 0) {
    return <p className="text-[11px]" style={{ color: MUTED }}>No platforms on the books.</p>;
  }
  return (
    <TableShell head={['PLATFORM', 'KIND', 'BILL OF MATERIALS']}>
      {platforms.map((platform, index) => (
        <tr key={platform.platformId} style={rowStyle(index)}>
          <td className={CELL} style={{ color: WHITE }}>{platform.name}</td>
          <td className={CELL} style={{ color: 'rgba(244,244,245,0.65)' }}>{platform.kind}</td>
          <td className={CELL} style={{ color: 'rgba(244,244,245,0.65)' }}>
            {platform.bom.length === 0
              ? 'No bill of materials recorded'
              : platform.bom
                  .map((line) => `${line.quantity} × ${nameOf.get(line.partId) ?? line.partId}`)
                  .join(' · ')}
          </td>
        </tr>
      ))}
    </TableShell>
  );
}

const ASSET_TONE: Record<string, string> = {
  ready: OK,
  degraded: WARN,
  down: DANGER,
  'in-maintenance': MUTED,
};

function AssetsTable() {
  const assets = useSupplyAssets();
  const locations = useSupplyLocations();
  const locationName = useMemo(
    () => new Map(locations.map((location) => [location.locationId, location.name])),
    [locations],
  );
  if (assets.length === 0) {
    return <p className="text-[11px]" style={{ color: MUTED }}>No aircraft on the asset register.</p>;
  }
  return (
    <TableShell head={['ASSET', 'PLATFORM', 'STATUS', 'LOCATION', 'BLOCKED ON']}>
      {assets.map((asset, index) => (
        <tr key={asset.assetId} style={rowStyle(index)}>
          <td className={CELL} style={{ color: WHITE }}>{asset.assetId}</td>
          <td className={CELL} style={{ color: 'rgba(244,244,245,0.65)' }}>{asset.platformId}</td>
          <td className={CELL} style={{ color: ASSET_TONE[asset.status] ?? MUTED }}>{asset.status}</td>
          <td className={CELL} style={{ color: 'rgba(244,244,245,0.65)' }}>
            {locationName.get(asset.locationId) ?? asset.locationId}
          </td>
          <td className={CELL} style={{ color: asset.blockingPartIds.length ? DANGER : MUTED }}>
            {asset.blockingPartIds.length ? asset.blockingPartIds.join(', ') : '—'}
          </td>
        </tr>
      ))}
    </TableShell>
  );
}

function OrdersTable() {
  const orders = useSupplyOrders();
  if (orders.length === 0) {
    return <p className="text-[11px]" style={{ color: MUTED }}>Nothing is on order.</p>;
  }
  return (
    <TableShell head={['ORDER', 'PART', 'VENDOR', 'QTY', 'DESTINATION', 'ETA', 'STATUS']}>
      {orders.map((order, index) => (
        <tr key={order.orderId} style={rowStyle(index)}>
          <td className={CELL} style={{ color: WHITE }}>{order.orderId}</td>
          <td className={CELL} style={{ color: 'rgba(244,244,245,0.65)' }}>{order.partId}</td>
          <td className={CELL} style={{ color: 'rgba(244,244,245,0.65)' }}>{order.vendorId}</td>
          <td className={`${CELL} tnum`} style={{ color: WHITE }}>{order.quantity}</td>
          <td className={CELL} style={{ color: 'rgba(244,244,245,0.65)' }}>{order.locationId}</td>
          <td className={`${CELL} tnum`} style={{ color: order.eta === null ? MUTED : 'rgba(244,244,245,0.65)' }}>
            {order.eta === null ? `${UNKNOWN} NO ETA` : new Date(order.eta * 1000).toISOString().slice(0, 10)}
          </td>
          <td className={CELL} style={{ color: 'rgba(244,244,245,0.65)' }}>{order.status}</td>
        </tr>
      ))}
    </TableShell>
  );
}

/** The layer has to be mounted from inside <MapCanvas>, which owns the context. */
function SupplyMapLayers() {
  useSupplyLayer();
  return null;
}

function SitesMap() {
  const locations = useSupplyLocations();
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[10.5px]" style={{ color: 'rgba(244,244,245,0.45)' }}>
        Depots, forward sites and what is in transit, at their coordinates. Hover or tap a site to read
        what is on its shelf. In-transit is drawn dimmer because a delivery is a promise, not
        stock on hand.
      </p>
      <div className="relative h-[min(380px,55dvh)] overflow-hidden rounded-[14px] phone:h-[min(300px,45dvh)]">
        <MapCanvas className="absolute inset-0">
          <SupplyMapLayers />
          <SupplySiteHoverLabel />
        </MapCanvas>
      </div>
      <p className="label-mono text-[9px]" style={{ color: MUTED }}>
        {locations.length} SITES ON THE MAP
      </p>
    </div>
  );
}

// ── The screen ──────────────────────────────────────────────────────────────

export function SupplyView(): ReactNode {
  const overview = useSupplyOverview();
  const error = useSupplyError();
  const parts = useSupplyParts();
  const canImport = useHasRole('operator');
  const [tab, setTab] = useState<Tab>('STOCK');

  useEffect(() => {
    void supplyActions.loadReference();
    void supplyActions.loadOverview();
    const id = window.setInterval(() => void supplyActions.loadOverview(), REFRESH_MS);
    return () => window.clearInterval(id);
  }, []);

  const nameOf = useMemo(() => {
    const byId = new Map(parts.map((part: Part) => [part.partId, part.name]));
    return (partId: string) => byId.get(partId) ?? partId;
  }, [parts]);

  return (
    <div
      className="screen-canvas absolute inset-0"
      style={{ background: 'radial-gradient(1200px 700px at 72% -10%, oklch(0.19 0 0), oklch(0.12 0 0))' }}
    >
      <main
        className="scroll-thin absolute flex flex-col gap-3 overflow-y-auto"
        style={{
          top: 90,
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(1180px, calc(100vw - 28px))',
        }}
        aria-label="Supply"
      >
        {error ? (
          <p
            className="rounded-[12px] border px-3 py-2 text-[11.5px]"
            role="status"
            style={{
              color: DANGER,
              borderColor: 'color-mix(in oklch, oklch(0.62 0.20 28) 45%, transparent)',
              background: 'color-mix(in oklch, oklch(0.62 0.20 28) 10%, transparent)',
            }}
          >
            {error}
          </p>
        ) : null}

        <CommanderRollup />

        <Panel
          title="WHAT IT'S COSTING"
          subtitle="Ranked by the sorties each shortage will cost before it is whole. A part with no supplier is first — an unknown that is grounding aircraft outranks a known wait."
          right={
            overview ? (
              <span className="label-mono ml-auto text-[9px]" style={{ color: MUTED }}>
                {overview.impacts.length} SHORTAGES GROUNDING AIRCRAFT
              </span>
            ) : undefined
          }
        >
          <ImpactList impacts={overview?.impacts ?? []} nameOf={nameOf} />
        </Panel>

        <Panel
          title="ORDER LIST"
          subtitle="This DRAFTS a requisition. It does not place one — no order is sent from this screen, and placing it is a human act outside this system."
        >
          <RecoveryList nameOf={nameOf} />
        </Panel>

        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))' }}>
          <Panel
            title="SUPPLY RISK"
            subtitle="Ranked findings. Higher severity is the thing that stops the unit, not the longest list."
          >
            <RiskList risks={overview?.risks ?? []} nameOf={nameOf} />
          </Panel>

          <Panel
            title="CAN WE FLY IT"
            subtitle="Pick a profile and say how many you want. The notes below the answer are shown verbatim."
          >
            <FeasibilityAsk />
          </Panel>
        </div>

        <Panel
          title="FORWARD OPERATOR DETAIL"
          subtitle="The rows behind the numbers above. A count nobody has physically verified is marked, never shown as confirmed."
          right={
            <nav className="ml-auto flex flex-wrap gap-1" aria-label="Supply detail">
              {TABS.map((name) => (
                <button
                  key={name}
                  type="button"
                  aria-current={tab === name ? 'page' : undefined}
                  onClick={() => setTab(name)}
                  className="rounded-[7px] px-2.5 py-1 label-mono text-[9px]"
                  style={{
                    background: tab === name ? 'rgba(255,255,255,0.14)' : 'transparent',
                    color: tab === name ? WHITE : MUTED,
                  }}
                >
                  {name}
                </button>
              ))}
            </nav>
          }
        >
          {tab === 'STOCK' ? <StockTable /> : null}
          {tab === 'PARTS' ? <PartsTable /> : null}
          {tab === 'VENDORS' ? <VendorsTable /> : null}
          {tab === 'PLATFORMS' ? <PlatformsTable /> : null}
          {tab === 'ASSETS' ? <AssetsTable /> : null}
          {tab === 'ORDERS' ? <OrdersTable /> : null}
          {tab === 'SITES' ? <SitesMap /> : null}
        </Panel>

        {/* Operator-gated to match the server, which refuses an import from a
            viewer. Offering the button and failing at the end would teach an
            operator that a refusal is something that happens after the work. */}
        {canImport ? (
          <Panel
            title="IMPORT A CATALOG"
            subtitle="A check reads the whole file and says exactly what it would do. Nothing is written until you press IMPORT, and an import lands whole or not at all — there is no half-applied catalog."
          >
            <SupplyImportPanel />
          </Panel>
        ) : null}
      </main>
    </div>
  );
}

export default SupplyView;
